import { theme } from '@contrast-security-inc/design-system-foundations'
import { css, cx } from '@emotion/css'
import React from 'react'
import { theme as deprecatedTheme } from '../../../../../styled'
import { BugTracker } from '../../../../../components/bug-tracker'
import { TicketUrl } from '../../../../../components/bug-tracker/bug-tracker-tooltip-content'
import {
  LibraryFormattedVersion,
  LibraryVersionUIProps
} from '../../library/library-formatted-version'
import { LibraryReleaseDate } from '../../library/library-release-date'
import { Library, LibraryVersion } from '../../../helpers/ui-interfaces'
import { LibraryNameLink } from '../../library/library-name'
import { LibraryGridPanelID } from '../../../helpers/drilldown-ui-interfaces'
import { Content } from './components/content'
import { NameCellRowData } from './interfaces'
import { FormattedMessage, useIntl } from 'react-intl'

interface NameCellProps {
  organizationID: string
  appLibraryStatusEnabled?: boolean
  rowActiveInPanel: boolean
  rowData: NameCellRowData
  applicationId?: string
  onClick?: (props?: any) => void | undefined
  setSelectedPanelTabId: React.Dispatch<
    React.SetStateAction<LibraryGridPanelID>
  >
}

export function NameCell({
  organizationID,
  appLibraryStatusEnabled,
  rowActiveInPanel,
  applicationId,
  onClick,
  setSelectedPanelTabId,
  rowData: {
    name,
    hash,
    language,
    group,
    ossEnabled,
    policies,
    tags,
    bugTrackers,
    versions,
    licenses,
    usage,
    id
  }
}: NameCellProps) {
  return (
    <Content
      appLibraryStatusEnabled={appLibraryStatusEnabled}
      rowActiveInPanel={rowActiveInPanel}
      name={name}
      tags={tags}
      organizationID={organizationID}
      hash={hash}
      language={language}
      policies={policies}
      bugTrackers={bugTrackers}
      ossEnabled={ossEnabled}
      versions={versions}
      group={group}
      licenses={licenses}
      usage={usage}
      applicationId={applicationId}
      onClick={onClick}
      libraryId={id}
      setSelectedPanelTabId={setSelectedPanelTabId}
    />
  )
}

interface LibraryNameCellBaseProps
  extends Omit<NameCellRowData, 'versions' | 'bugTrackers' | 'id' | 'tags'> {
  setIsOpen?: (value: boolean) => void
  organizationID: string
  versions: {
    current: {
      version: LibraryVersion
      releaseDate?: string
    }
  }
  className?: string
  onClick?: (props?: any) => void
  libraryId?: Library['id']
  setSelectedPanelTabId?: React.Dispatch<
    React.SetStateAction<LibraryGridPanelID>
  >
}

export function LibraryNameCellBase({
  organizationID,
  name,
  hash,
  versions: { current },
  policies,
  usage,
  className,
  onClick,
  libraryId,
  setSelectedPanelTabId
}: LibraryNameCellBaseProps) {
  const { restrictedByOrg, invalidVersion, restrictedLicense } = policies
  const hasRestriction =
    restrictedByOrg || !!invalidVersion || !!restrictedLicense

  return (
    <LibraryNameLink
      organizationID={organizationID}
      name={name}
      hash={hash}
      version={current.version}
      hasRestriction={hasRestriction}
      usage={usage}
      className={className}
      onClick={onClick}
      libraryId={libraryId}
      setSelectedPanelTabId={setSelectedPanelTabId}
    />
  )
}

const currentVersionContainerCss = css`
  font-family: ${deprecatedTheme.fontFamilies.secondary};
  font-size: 12px;
  color: ${theme.color.neutral60};
  display: flex;
`

export function CurrentVersion({
  version,
  releaseDate,
  language
}: {
  releaseDate?: string
} & Pick<LibraryVersionUIProps, 'language' | 'version'>) {
  const { formatMessage } = useIntl()
  return (
    <div className={currentVersionContainerCss}>
      {!version || version === '?' ? (
        formatMessage({ id: 'title_libraries_unknown' })
      ) : (
        <React.Fragment>
          <LibraryFormattedVersion version={version} language={language} />
          <LibraryReleaseDate releaseDate={releaseDate} />
        </React.Fragment>
      )}
    </div>
  )
}

export function TooltipContentBugTrackers({
  bugTrackers,
  overrideBugtrackerListCss
}: {
  bugTrackers: BugTracker[]
  overrideBugtrackerListCss?: string
}) {
  const { formatMessage } = useIntl()
  return (
    <div>
      <div className="tooltip-title">
        {formatMessage({ id: 'title_sentToBugtracker' })}
      </div>
      <BugTrackers
        bugTrackers={bugTrackers}
        overrideBugtrackerListCss={overrideBugtrackerListCss}
      />
    </div>
  )
}

const bugTrackersCss = css`
  list-style-type: none;
  padding: 0;
  margin: 0;
`

const bugTrackerMessageCss = css`
  padding-right: 5px;
`

function BugTrackers({
  bugTrackers,
  overrideBugtrackerListCss
}: {
  bugTrackers: BugTracker[]
  overrideBugtrackerListCss?: string
}) {
  return (
    <ul className={cx(bugTrackersCss, overrideBugtrackerListCss)}>
      {bugTrackers.map(({ name, type, ticketKey, ticketUrl }, index) => (
        <li key={index}>
          <FormattedMessage
            id="variable_libraries_trackedBugtracker"
            values={{ bugtrackerName: name, bugtrackerType: type }}
            children={(msg) => (
              <span className={bugTrackerMessageCss}>{msg}</span>
            )}
          />
          <TicketUrl type={type} ticketKey={ticketKey} ticketUrl={ticketUrl} />
        </li>
      ))}
    </ul>
  )
}
